
/* ----- Vue ----- */
import { Component, Vue, Prop, Watch, Ref, Emit } from "vue-property-decorator"

/* ----- Components ----- */
import SimpleFieldSelect from "@/components/Config/Fields/SimpleFieldSelect.vue"
import VueSelect from "vue-select"

/* ----- Types ----- */
import { DataType, InsightDetails } from "@/types/insights"
import { InsightPresetKeys, InsightPreset, InsightPresetParameter } from "@/types/insights/presets/insightPreset"
import HelpIcon from "../New/Page/PageComponents/HelpIcon.vue"

@Component({
	components: {
		"simple-field-select": SimpleFieldSelect,
		"v-select": VueSelect,
		"help-icon": HelpIcon
	}
})
export default class InsightPresetConfig extends Vue {
	/* ----- Props ----- */
	@Prop({ type: Object, required: true }) private readonly value!: { [key: string]: unknown }
	@Prop({ type: Object, required: true }) private readonly insight!: InsightDetails
	@Prop({ type: String, required: true }) private readonly presetKey!: InsightPresetKeys
	@Prop({ type: Array, required: true }) private readonly presets!: InsightPreset[]

	/* ----- Constants ----- */
	private readonly DataType = DataType

	/* ----- Data ----- */
	private isMounted: boolean = false

	/* ----- Lifecycle ----- */

	private async mounted () {
		if (this.presets.length === 0) {
			this.fetchPresets()
		}
		this.isMounted = true
	}

	/* ----- Computed Properties ----- */

	private get isLoading () {
		return !(this.isMounted)
	}

	private get presetParameters (): InsightPresetParameter[] {
		if (!this.isMounted || !this.presetKey) {
			return []
		}

		const preset = this.presets.find(p => p.id === this.presetKey)
		return preset?.parameters ?? []
	}

	/* ----- Vue-related Methods ----- */

	@Watch("presetKey", { immediate: true })
	private onPresetKeyChanged (newKey: InsightPresetKeys, oldKey: InsightPresetKeys) {
		const preset = this.presets.find(p => p.id === newKey)
		if (!preset) {
			console.error("InsightPresetConfig: presetKey changed to unknown preset", newKey)
		}
	}

	@Emit("input")
	private setValue (paramId: string, newValue: unknown, dataType?: DataType) {
		const newValueParsed = this.parseValue(newValue, dataType ?? DataType.string)

		const newParameters = {
			...(this.value ?? {}),
			[paramId]: newValueParsed
		}

		return newParameters
	}

	@Emit("fetchPresets")
	private fetchPresets () {
	}

	/* ----- Other Methods ----- */

	private parseValue (value: unknown, dataType: DataType): unknown {
		if (dataType === DataType.number && typeof value === "string" && value) {
			return parseFloat(value)
		}

		return value
	}

	private getPlaceholderText (param: InsightPresetParameter): string {
		if (param.placeholder) {
			return param.placeholder
		} else if (this.isLoading) {
			return "Loading..."
		}

		return param.isRequired ? "Required" : "Optional"
	}
}
